import { memo, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import navIcon from "@/assets/nav-icon.svg";
import { Plus, Bot, Settings, User, TrendingUp } from "lucide-react";
import { trackSignInPromptShown, trackSignInClicked, trackSettingsOpened } from "@/analytics";
import { Urls } from "@/lib/navigation";

import type { MobileIslandConfig } from "./types";

export const MobileChatIsland = memo(function MobileChatIsland({
  config,
}: {
  config: MobileIslandConfig;
}) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    if (open && !config.isAuthenticated) {
      trackSignInPromptShown('mobile_island');
    }
  }, [open, config.isAuthenticated]);

  if (open) {
    return (
      <div className="absolute inset-0 z-10 flex items-center gap-2.5">
        <button
          type="button"
          onClick={toggle}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/[0.06] bg-zinc-900/80 backdrop-blur-xl transition-colors active:bg-white/[0.08]"
        >
          <img
            src={navIcon}
            alt="Close"
            className="h-[18px] w-[18px] object-contain invert"
          />
        </button>
        <div className="flex min-h-[44px] flex-1 items-center justify-evenly overflow-hidden rounded-2xl border border-white/[0.06] bg-zinc-900/60 px-2 backdrop-blur-xl">
          <button
            type="button"
            onClick={() => { config.onHome(); toggle(); }}
            className="flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/[0.08]"
          >
            <Plus className="h-[22px] w-[22px] text-zinc-400" />
          </button>

          {config.isAuthenticated ? (
            <>
              <Link
                to={Urls.agents()}
                onClick={toggle}
                className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/[0.08]"
              >
                <Bot className="h-[22px] w-[22px] text-zinc-400" />
                {config.runningCount > 0 && (
                  <span className="absolute right-0.5 top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[9px] font-medium text-white">
                    {config.runningCount}
                  </span>
                )}
              </Link>

              <Link
                to={Urls.settings()}
                onClick={() => { trackSettingsOpened('general'); toggle(); }}
                className="flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/[0.08]"
              >
                <Settings className="h-[22px] w-[22px] text-zinc-400" />
              </Link>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { trackSignInClicked('mobile_island'); config.onLogin(); toggle(); }}
              className="flex h-9 items-center gap-1.5 rounded-full px-3 transition-colors active:bg-white/[0.08]"
            >
              <User className="h-[20px] w-[20px] text-zinc-400" />
              <span className="text-[13px] text-zinc-400">Sign in</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.06] bg-zinc-900/80 backdrop-blur-xl transition-colors active:bg-white/[0.08]"
      >
        <img
          src={navIcon}
          alt="Menu"
          className="h-[18px] w-[18px] object-contain invert"
        />
      </button>
      {config.artifactMini?.visible && (
        <button
          type="button"
          onClick={config.artifactMini.onOpen}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.06] bg-zinc-900/80 backdrop-blur-xl transition-colors active:bg-white/[0.08]"
        >
          <TrendingUp className="h-[22px] w-[22px] text-zinc-400" />
        </button>
      )}
    </>
  );
});
